import { GridList, GridListItem } from 'react-aria-components';
import { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import SimpleBar from 'simplebar-react';

import Loader from '@/components/Loader';
import Badge from '@/design_system/Badge';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAdd from '@/icons/Add.svg';
import IconWarning from '@/icons/Warning.svg';
import { useArticleName } from '@/models/article';
import { ArticleNameCell } from '@/routes/Shipments/Shipment/components/ShipmentForm/components/ArticleSelect/components/ArticleNameCell';
import { createBEMClasses } from '@/utils/classname';

import { ShipmentFormArticle } from '../../ArticleSelect';

import './ShippableArticlesSection.css';

const { block, element } = createBEMClasses('shippable-articles-section');

export const ShippableArticlesSection = ({
  unaddedArticles,
  noItemsMessage,
  onAddArticle,
  isFetching,
}: {
  unaddedArticles: ShipmentFormArticle[];
  noItemsMessage: MessageDescriptor | null;
  onAddArticle: (articleId: string) => void;
  isFetching: boolean;
}) => {
  const { _ } = useLingui();

  if (isFetching) {
    return (
      <Stack alignItems="center" justifyContent="center">
        <Loader style={{ height: '40px', width: '40px' }} delay={0} />
      </Stack>
    );
  }

  return (
    <Stack className={block()}>
      <Paragraph className={element('label')}>
        <Trans id="shipments.new.articles.shippable.label">Shippable</Trans>
      </Paragraph>
      <SimpleBar className={element('scrollbar')} autoHide={false}>
        {noItemsMessage ? (
          <Paragraph color="secondary" style={{ paddingLeft: '0.5rem' }}>
            {_(noItemsMessage)}
          </Paragraph>
        ) : (
          <GridList
            aria-label={_(
              msg({
                id: 'shipments.new.articles.shippable.label',
                message: 'Shippable',
              })
            )}
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {unaddedArticles.map((article) => (
              <ShippableArticleRow key={article.id} article={article} onAdd={onAddArticle} />
            ))}
          </GridList>
        )}
      </SimpleBar>
    </Stack>
  );
};

const ShippableArticleRow = ({
  article,
  onAdd,
}: {
  article: ShipmentFormArticle;
  onAdd: (articleId: string) => void;
}) => {
  const articleName = useArticleName({ article });

  return (
    <GridListItem
      className={element('shippable-article-row')}
      textValue={articleName}
      id={article.id}
    >
      <ArticleNameCell
        article={article}
        photoSize="large"
        badge={
          (article.quoteRefusedByTheClient || article.quoteRefusedDuringAnalysis) &&
          article.atEntityId !== article.request?.store?.id ? (
            <Badge size="small" color="warning" borderRadius="full" hasBorder>
              <IconWarning />
              {article.analysisRefusedAt ? (
                <Trans id="shipments.new.articles.job-refused.badge">Job refused</Trans>
              ) : (
                <Trans id="shipments.new.articles.quote-refused.badge">Quote refused</Trans>
              )}
            </Badge>
          ) : undefined
        }
      />

      <AddArticleButton articleId={article.id} onAdd={onAdd} />
    </GridListItem>
  );
};

const AddArticleButton = ({
  articleId,
  onAdd,
}: {
  articleId: string;
  onAdd: (articleId: string) => void;
}) => {
  const { _ } = useLingui();

  return (
    <Button
      size="medium"
      variant="secondary"
      iconOnly
      onPress={() => onAdd(articleId)}
      ariaLabel={_(msg({ id: 'shipments.new.articles.add', message: 'Add item' }))}
      style={{
        marginLeft: 'auto',
        marginRight: '0.5rem',
      }}
    >
      <IconAdd />
    </Button>
  );
};
